import { useMemo, type ReactNode } from "react";
import {
  CopilotChatConfigurationProvider,
  CopilotKitProvider,
  CopilotSidebar,
} from "@copilotkit/react-core/v2";
import "@copilotkit/react-ui/v2/styles.css";
import { useAuth } from "@/contexts/AuthContext";
import { buildOneThingAgent } from "@/lib/copilotAgent";
import { CopilotChatHeaderControls } from "./CopilotChatHeaderControls";
import { CopilotLauncherTooltip } from "./CopilotLauncherTooltip";
import { COPILOT_SIDEBAR_WIDTH } from "./copilot-sidebar-open-marker";
import { CopilotProactiveWelcome } from "./CopilotProactiveWelcome";
import { CopilotToolRenderers } from "./CopilotToolRenderers";
import { CopilotFrontendTools } from "./actions/CopilotFrontendTools";
import { CopilotAmbientContextBridge } from "./context/CopilotAmbientContextBridge";
import { CopilotEntityProvider } from "./context/CopilotEntityContext";

/**
 * Sidebar header: keep the default title + close button, and inject the
 * "New chat" button + infinite-scroll loader between them. The `children`
 * render-prop hands back the slot elements so we control the layout.
 */
const CHAT_HEADER = {
  children: ({
    titleContent,
    closeButton,
  }: {
    titleContent: ReactNode;
    closeButton: ReactNode;
  }) => (
    <div className="flex w-full items-center gap-2">
      <div className="flex-1 text-center">{titleContent}</div>
      <div className="flex items-center gap-1">
        <CopilotChatHeaderControls />
        {closeButton}
      </div>
    </div>
  ),
} as const;

/**
 * Labels for every user-facing string the CopilotSidebar exposes.
 * Keys must match @copilotkit/react-core/v2 CopilotChatDefaultLabels —
 * unknown keys are silently dropped and the English default renders.
 */
const CHAT_LABELS = {
  modalHeaderTitle: "OneThing Assistant",
  welcomeMessageText: "How can I help you today?",
  chatInputPlaceholder: "Message Infina1t…",
  chatDisclaimerText: "Infina1t can make mistakes. Check important info.",
  chatToggleOpenLabel: "Open OneThing assistant",
  chatToggleCloseLabel: "Close OneThing assistant",
} as const;

/**
 * Mounts CopilotKit (v2) with the OneThing AG-UI agent registered as a
 * self-managed, client-side agent — no CopilotKit runtime server exists in
 * this Vite SPA; the agent streams straight from the BFF's /api/chat/agui.
 *
 * The provider always mounts so hooks like `useCopilotKit()` resolve
 * anywhere in the tree; the chat surface itself is auth-gated. Keying both
 * the agent and the provider on `user.pid` resets the in-memory
 * conversation buffer across login/logout so one user's chat never bleeds
 * into the next session (the BFF keys the real conversation by session
 * cookie server-side).
 */
export function CopilotProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  // user.pid is an intentional dependency: a fresh agent per login session
  // drops the previous user's in-memory message buffer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agent = useMemo(() => buildOneThingAgent(), [user?.pid]);

  return (
    <CopilotKitProvider
      key={user?.pid ?? "guest"}
      selfManagedAgents={{ default: agent }}
    >
      {/* Outer chat config so pages (outside the sidebar's own provider) can open
          the panel via useCopilotChatConfiguration().setModalOpen. The explicit
          `false` is required: the sidebar's inner provider only follows parent
          changes, so a parent defaulting to `true` would never flip to open. */}
      <CopilotChatConfigurationProvider agentId="default" isModalDefaultOpen={false}>
        <CopilotEntityProvider>
          {children}
          {/* Must live inside CopilotEntityProvider — it consumes CopilotEntityContext,
              which only flows down the tree, not to siblings. */}
          {isAuthenticated && <CopilotAmbientContextBridge />}
        </CopilotEntityProvider>
        {isAuthenticated && <CopilotToolRenderers />}
        {isAuthenticated && <CopilotFrontendTools />}
        {isAuthenticated && (
          <CopilotSidebar
            agentId="default"
            defaultOpen={false}
            labels={CHAT_LABELS}
            width={COPILOT_SIDEBAR_WIDTH}
            welcomeScreen={CopilotProactiveWelcome}
            header={CHAT_HEADER}
          />
        )}
        {isAuthenticated && <CopilotLauncherTooltip />}
      </CopilotChatConfigurationProvider>
    </CopilotKitProvider>
  );
}
